import { Component } from "@angular/core";
import { ColDef, GridReadyEvent } from "@ag-grid-community/core";
import {
  ActiveStatusEnum,
  Feature,
  FeatureChoice,
  Manufacturer,
  Product,
  ProductFeatureChoice,
  ProductType,
  ProductTypeAlt,
  ProductTypeFeatureMap,
} from "app/model/entities/entity-model";
import { FeatureKindEnum } from "app/model/entities/-feature-kind-enum";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "app/shared/domain.service";
import { EntityState } from "breeze-client";
import _ from "lodash";
import { AgStyleFns } from "@/shared/ag-style-fns";
import { UtilFns } from "@/shared/util-fns";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import { ManufacturerFeedStatusEnum } from "app/model/entities/enums";
import { StagedFeedApiService } from "@/admin/staged-feed-api.service";
import { FeedBuildColorDTO, FeedBuildSizeDTO } from "@/admin/staged-feed.models";

export interface ProductBuilderDialogParams extends DialogParams {
  productType: ProductType;
}

type GetProductFn = (params: any) => Product;

@Component({
  templateUrl: "./product-builder-dialog.component.html",
  standalone: false,
})
export class ProductBuilderDialogComponent extends DialogBaseComponent<ProductBuilderDialogParams> {
  productType: ProductType;
  productTypeFeatureMaps: ProductTypeFeatureMap[] = [];

  features: Feature[] = [];
  selectedFeature?: Feature;

  // key is featureId
  savedFeatureChoiceMap = new Map<Feature, FeatureChoice[]>();

  featureChoiceGridOptions: GridOptionsExt = {};
  featureChoices: FeatureChoice[] = [];
  selectedFeatureChoices: FeatureChoice[] = [];

  productGridOptions: GridOptionsExt = {};
  products: Product[] = [];

  /** Null means the manufacturer has no feed, so colors are not restricted. */
  feedColorNames: Set<string> | null = null;

  /**
   * What the manufacturer publishes for this style, as colors each carrying their own sizes.
   * Null means nothing restricts the build and the manual pickers stand.
   */
  feedColors: FeedBuildColorDTO[] | null = null;

  /** Color choices the operator wants to carry. Starts as everything the feed offers. */
  selectedFeedColorIds = new Set<number>();

  /**
   * How many products Generate would actually create - the published pairs that do not exist yet.
   *
   * Held rather than computed in a getter: on a style like PC54 it is 738 pairs against every
   * existing product, and the template would ask for it on every change detection pass.
   */
  feedNewCount = 0;

  constructor(
    public config: DynamicDialogConfig<ProductBuilderDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private stagedFeedApi: StagedFeedApiService
  ) {
    super(config, dialogRef, domainService);
    this.prepare();
  }

  private async prepare() {
    this.productType = this.params.productType;

    // Refresh productType to ensure we have all related data
    this.productType = await this.dbQueryService.getProductType(this.productType.id);
    this.productTypeFeatureMaps = this.productType.productTypeFeatureMaps;

    this.feedColorNames = await this.loadFeedColorNames();
    await this.loadFeedPairs();

    Object.assign(this.config, {
      header: "Product Builder - " + this.productType.style,
      width: "50vw",
      height: "70vh",
    });

    this.featureChoiceGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onFeatureChoiceGridReady,
      rowSelection: RowSelectionMultipleCheckbox,
      onSelectionChanged: this.onFeatureChoicesSelectedChanged,
      getRowId: (params) => {
        return params.data.choiceValue;
      },
      rowModelType: "clientSide",
    });

    this.productGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProductGridReady,
      rowModelType: "clientSide",
    });

    await this.refreshProducts();

    this.isPageReady = true;
  }

  async onFeatureChoiceGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getFeatureChoiceColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  onFeatureChoicesSelectedChanged(event: unknown) {
    const selectedRows = this.featureChoiceGridOptions.apiX?.getSelectedRows() as FeatureChoice[];
    if (selectedRows.length > 0) {
      this.selectedFeatureChoices = selectedRows;
    } else {
      this.selectedFeatureChoices = [];
    }
    this.saveFeatureChoices(this.selectedFeature, this.selectedFeatureChoices);
  }

  getFeatureChoiceColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      {
        headerName: "Feature Choice",
        field: "choiceValue",
        filter: "agTextColumnFilter",
      },
    ];
    const sortModel: ISortModel = [{ colId: "displayOrder", sort: "asc" }];
    return [colDefs, sortModel] as const;
  }

  async onProductGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getProductColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);

    this.showProducts();
  }

  getProductColDefsAndSortModel() {
    const featureColDefs = this.getFeatureColDefs(this.features);

    const colDefs: ColDef[] = [
      AgStyleFns.getEntityStateColDef(),
      ...featureColDefs,
      {
        // The product's own status, which the State column does not say - that one reports whether
        // Breeze has the row as added or modified. A product retired because the manufacturer does
        // not make it still lists here, and without this it reads as one that is on sale.
        headerName: "Status",
        colId: "activeStatus",
        sortable: false,
        valueGetter: (params) => this.statusLabel((params.data as Product)?.activeStatusId),
        cellStyle: (params) => {
          const statusId = (params.data as Product)?.activeStatusId;
          if (statusId === ActiveStatusEnum.Active) return null;
          if (statusId === ActiveStatusEnum.Inactive) return { color: "var(--qb-status-inactive-color, #dc2626)", fontStyle: "italic" };
          if (statusId === ActiveStatusEnum.Phaseout) return { color: "var(--qb-status-phaseout-color, #ea580c)", fontStyle: "italic" };
          return { color: "var(--p-text-muted-color, #6b7280)", fontStyle: "italic" };
        },
        ...AgFns.fixWidth(110),
      },
      {
        ...AgFns.createButtonProps("", this.onDeleteProduct.bind(this), {
          canDisplay: this.canDeleteProduct.bind(this),
          label: "Detach",
          icon: "pi pi-trash",
        }),
        ...AgFns.fixWidth(100),
      },
    ];

    const sortModel: ISortModel = this.features.map((f) => {
      return { colId: f.name, sort: "asc" };
    });
    return [colDefs, sortModel] as const;
  }

  /** Read off the enum rather than the navigation, which this query does not expand. */
  private statusLabel(id: number | undefined) {
    switch (id) {
      case ActiveStatusEnum.Active:
        return "Active";
      case ActiveStatusEnum.Hold:
        return "Hold";
      case ActiveStatusEnum.Phaseout:
        return "Phaseout";
      case ActiveStatusEnum.Inactive:
        return "Inactive";
      default:
        return "";
    }
  }

  getFeatureColDefs(features: Feature[], getProductFn: GetProductFn = (params) => params.data as Product) {
    const featureColDefs = features.map((x) => this.createFeatureColDef(x, getProductFn));
    return featureColDefs;
  }

  createFeatureColDef(feature: Feature, getProductFn: GetProductFn = (params) => params.data as Product) {
    return {
      headerName: feature?.getDisplayName(),
      colId: "f" + feature?.name,
      maxWidth: 140,
      editable: false,
      sortable: false,
      valueGetter: (params) => {
        const prod = getProductFn(params);
        if (prod == null) return;
        const pfc = prod.productFeatureChoices.find((fc) => fc.featureChoice?.featureId == feature.id);
        return pfc?.featureChoice.choiceValue ?? "";
      },
    } as ColDef;
  }

  private async refreshProducts() {
    if (this.productType == null) {
      return;
    }
    this.products = await this.dbQueryService.getProductsForProductType(this.productType.id);
    this.products = _.sortBy(this.products, (p) => p.getSortOrder());

    const orderedProductTypesFeatures = _.orderBy(this.productTypeFeatureMaps, (x) => x.displayOrder);
    this.features = orderedProductTypesFeatures.map((ptf) => ptf.feature);
    this.showProducts();
    this.refreshFeedCounts();
  }

  private showProducts() {
    if (this.productGridOptions.apiX == null) return;

    const [colDefs, sortModel] = this.getProductColDefsAndSortModel();
    AgFns.initGrid(this.productGridOptions, colDefs, sortModel);
    AgFns.refreshGrid(this.productGridOptions, this.products);
  }

  /**
   * Lower-cased names the manufacturer publishes for this style, or null when the manufacturer
   * has no feed — null meaning "do not restrict" rather than "restrict to nothing".
   *
   * Reads the staged feed live rather than any stored match, for the same reason the Change
   * Color dialog does: the match written during a sweep goes stale, and a color wrongly absent
   * here would silently block a product nobody could then build.
   */
  private async loadFeedColorNames(): Promise<Set<string> | null> {
    const mfr = this.productType.manufacturer;
    if (mfr == null || mfr.manufacturerFeedStatusId === ManufacturerFeedStatusEnum.None) return null;

    const detail = await this.stagedFeedApi.getStyleColorsForProductType(this.productType.id);
    if (detail == null) return null;
    // BOTH published spellings. They are not always the same string - SanMar sends J763 as
    // 'Navy/ Black' and 'Navy/Black' - and keeping one hid a color Feed Health calls published.
    return new Set(
      detail
        .flatMap((c) => [c.StandardColorName, c.ColorName])
        .map((n) => (n ?? "").trim().toLowerCase())
        .filter((n) => n.length > 0)
    );
  }

  /**
   * What the manufacturer publishes, as colors each carrying their own sizes - or, for a
   * manufacturer that requires no Color such as Red Kap, one group of sizes.
   *
   * The server decides whether the feed can describe this style, from the manufacturer's required
   * feature kinds, and answers null when it cannot - a style carrying a feature the manufacturer
   * knows nothing about keeps the manual pickers, because a generated product would be missing a
   * choice. Deciding it here from a literal Size + Color left every Red Kap style on the manual
   * pickers, free to build any size on the list.
   */
  private async loadFeedPairs() {
    const mfr = this.productType.manufacturer;
    if (mfr == null || mfr.manufacturerFeedStatusId === ManufacturerFeedStatusEnum.None) return;

    const maps = this.productType.productTypeFeatureMaps ?? [];
    const sizeFeatureId = maps.find((m) => m.feature?.featureKindId === FeatureKindEnum.Size)?.featureId;
    const pairs = await this.stagedFeedApi.getBuildablePairs(this.productType.id, sizeFeatureId);
    if (pairs == null || pairs.length === 0) return;

    this.feedColors = pairs;
    // Everything on, because the common case is carrying the style as the manufacturer offers it.
    // Unticking is the exception and is a purchasing decision, so it stays the operator's.
    this.selectedFeedColorIds = new Set(pairs.map((c) => c.FeatureChoiceId));
  }

  /** The existing products decide what is new, so this has to follow every change to either. */
  private refreshFeedCounts() {
    if (this.isFeedDriven) this.recomputeFeedNewCount();
  }

  /** Whether the feed answers for this style, so the pickers can stand down. */
  get isFeedDriven(): boolean {
    return this.feedColors != null;
  }

  /** The manufacturer requires no Color, so the one group is the style's sizes. See FeedBuildColorDTO. */
  get isSizeOnlyFeed(): boolean {
    return this.feedColors?.length === 1 && this.feedColors[0].FeatureChoiceId === 0;
  }

  /** The choices one built product carries. No color id on a size-only manufacturer. */
  private feedChoiceIds(c: FeedBuildColorDTO, s: FeedBuildSizeDTO): number[] {
    return c.FeatureChoiceId === 0 ? [s.FeatureChoiceId] : [c.FeatureChoiceId, s.FeatureChoiceId];
  }

  isFeedColorSelected(c: FeedBuildColorDTO): boolean {
    return this.selectedFeedColorIds.has(c.FeatureChoiceId);
  }

  onFeedColorToggled(c: FeedBuildColorDTO, isOn: boolean) {
    if (isOn) {
      this.selectedFeedColorIds.add(c.FeatureChoiceId);
    } else {
      this.selectedFeedColorIds.delete(c.FeatureChoiceId);
    }
    this.recomputeFeedNewCount();
  }

  onAllFeedColorsToggled(isOn: boolean) {
    this.selectedFeedColorIds = isOn
      ? new Set((this.feedColors ?? []).map((c) => c.FeatureChoiceId))
      : new Set<number>();
    this.recomputeFeedNewCount();
  }

  /**
   * An existing product counts whatever its status. A size retired because the manufacturer stopped
   * making it must not be silently rebuilt by the next Generate, and reviving it is a decision for
   * whoever retired it rather than a side effect of building the style.
   */
  private recomputeFeedNewCount() {
    let count = 0;
    this.selectedFeedColors.forEach((c) =>
      c.Sizes.forEach((s) => {
        if (!this.isDupByChoiceIds(this.feedChoiceIds(c, s))) count++;
      }),
    );
    this.feedNewCount = count;
  }

  get areAllFeedColorsSelected(): boolean {
    const all = this.feedColors ?? [];
    return all.length > 0 && all.every((c) => this.selectedFeedColorIds.has(c.FeatureChoiceId));
  }

  /** How many products Generate would build, before duplicates are taken out. */
  get feedBuildCount(): number {
    return _.sumBy(this.selectedFeedColors, (c) => c.Sizes.length);
  }

  get selectedFeedColors(): FeedBuildColorDTO[] {
    return (this.feedColors ?? []).filter((c) => this.selectedFeedColorIds.has(c.FeatureChoiceId));
  }

  /**
   * One product per pair the manufacturer actually publishes, never a cross of two lists.
   *
   * 52 of 613 staged styles are not a full rectangle - some colors run fewer sizes than others -
   * so crossing the colors by the sizes would invent products nobody makes even when every color
   * and every size in the lists is real.
   */
  async onGenerateFromFeed() {
    const pairs: number[][] = [];
    this.selectedFeedColors.forEach((c) =>
      c.Sizes.forEach((s) => pairs.push(this.feedChoiceIds(c, s))),
    );

    const fresh = pairs.filter((ids) => !this.isDupByChoiceIds(ids));
    const newProducts = fresh.map((ids) => this.createProductFromChoiceIds(ids));
    this.products.push(...newProducts);
    this.showProducts();
    this.refreshFeedCounts();

    const skipped = pairs.length - fresh.length;
    if (skipped > 0) {
      this.toastr.warning(`${newProducts.length} product(s) created; ${skipped} already existed.`);
    } else {
      this.toastr.success(`${newProducts.length} product(s) created.`);
    }
  }

  async onFeatureClick(event: unknown) {
    await UtilFns.wait(100);
    if (this.selectedFeature == null) {
      this.selectedFeature = this.features[0];
    }

    let featureChoices = this.selectedFeature.featureChoices.filter((x) => x.activeStatusId == ActiveStatusEnum.Active);

    // Where the manufacturer owns the catalog, only offer colors it actually publishes for
    // this style. Without this the generator will happily build a product in a color the
    // manufacturer does not make, which is the drift the feed exists to stop.
    if (this.feedColorNames != null && this.selectedFeature.featureKindId === FeatureKindEnum.Color) {
      featureChoices = featureChoices.filter((fc) => this.feedColorNames.has((fc.choiceValue ?? "").trim().toLowerCase()));
    }

    this.featureChoices = _.sortBy(featureChoices, (fc) => fc.displayOrder);
    await AgFns.refreshGrid(this.featureChoiceGridOptions, this.featureChoices);

    this.selectedFeatureChoices = this.getSavedFeatureChoices(this.selectedFeature);
    this.selectedFeatureChoices.forEach((fc) => {
      const rowNode = this.featureChoiceGridOptions.apiX.getRowNode(fc.choiceValue);
      rowNode.setSelected(true);
    });
  }

  formatSelectedChoices(feature: Feature) {
    const selectedFeatureChoices = this.getSavedFeatureChoices(feature);
    if (selectedFeatureChoices.length > 0) {
      return selectedFeatureChoices.map((fc) => fc.choiceValue).join(", ");
    } else {
      return "";
    }
  }

  saveFeatureChoices(feature: Feature, featureChoices: FeatureChoice[]) {
    this.savedFeatureChoiceMap.set(feature, featureChoices);
  }

  getSavedFeatureChoices(feature: Feature) {
    return this.savedFeatureChoiceMap.get(feature) ?? [];
  }

  canGenerate(): boolean {
    if (this.isFeedDriven) return this.feedNewCount > 0;

    return this.features.every((f) => {
      const choices = this.savedFeatureChoiceMap.get(f);
      return choices && choices.length > 0;
    });
  }

  async onGenerate() {
    const fcCombs = this.createCombinatorics();
    const newProducts = this.generateNewProducts(fcCombs);
    this.products.push(...newProducts);
    this.showProducts();
  }

  createCombinatorics() {
    let fcCombs: FeatureChoice[][] = [];
    this.savedFeatureChoiceMap.forEach((fcs, feature) => {
      if (fcCombs.length == 0) {
        fcCombs = fcs.map((x) => [x]);
      } else {
        const newCombs: FeatureChoice[][] = [];
        fcs.forEach((fc) => {
          fcCombs.forEach((fcComb) => {
            newCombs.push([...fcComb, fc]);
          });
        });
        fcCombs = newCombs;
      }
    });
    return fcCombs;
  }

  private generateNewProducts(fcCombs: FeatureChoice[][]) {
    const nonDupFcCombs = fcCombs.filter((fcComb) => {
      return !this.isDupProduct(fcComb);
    });

    const newProducts = nonDupFcCombs.map((fcComb) => this.createProduct(fcComb));
    if (newProducts.length != fcCombs.length) {
      this.toastr.warning(`${newProducts.length} product(s) created; ${fcCombs.length - newProducts.length} other(s) were duplicates`);
    } else {
      this.toastr.success(`${newProducts.length} product(s) created`);
    }
    return newProducts;
  }

  isDupProduct(featureChoices: FeatureChoice[]) {
    return this.isDupByChoiceIds(featureChoices.map((fc) => fc.id));
  }

  /** Keyed on choice ids rather than entities, so the feed path can use it with ids alone. */
  private isDupByChoiceIds(choiceIds: number[]) {
    const fcIdSet = new Set(choiceIds);
    return this.products.some((p) => {
      if (fcIdSet.size != p.productFeatureChoices.length) return false;
      return p.productFeatureChoices.every((pfc) => fcIdSet.has(pfc.featureChoiceId));
    });
  }

  createProduct(fcComb: FeatureChoice[]) {
    return this.createProductFromChoiceIds(fcComb.map((fc) => fc.id));
  }

  private createProductFromChoiceIds(choiceIds: number[]) {
    const struct = <Partial<Product>>{
      activeStatusId: ActiveStatusEnum.Active,
      productTypeId: this.productType.id,
      modTs: new Date(),
      modUserInit: this.authUser.initials,
    };

    const product = this.dbSaveService.createEntity(Product, struct, EntityState.Added);
    choiceIds.forEach((id) =>
      this.dbSaveService.createEntity(
        ProductFeatureChoice,
        {
          productId: product.id,
          featureChoiceId: id,
        },
        EntityState.Added
      )
    );
    return product;
  }

  canDeleteProduct(product: Product) {
    return product.entityAspect.entityState.isAdded();
  }

  onDeleteProduct(product: Product) {
    const aspect = product.entityAspect;
    if (aspect.entityState.isAdded()) {
      aspect.setDetached();
    } else {
      aspect.setDeleted();
    }
    _.remove(this.products, (p) => p === product);
    this.showProducts();
    this.refreshFeedCounts();
  }

  async ok() {
    if (!this.uow.hasChanges()) {
      this.dialogRef.close(false);
      return;
    }

    try {
      await this.dbSaveService.saveChanges();
      this.toastr.success("Products saved");
      this.dialogRef.close(true);
    } catch (e) {
      this.uow.rollback();
    }
  }

  async cancel() {
    this.isPageReady = false;
    this.uow.rollback();
    this.dialogRef.close(false);
  }
}
